/**
 * Seed helpers that turn the backend's FINAL rendered content into a
 * ProseMirror doc for the enrollment-preview editors.
 *
 * Why this exists: the renderer serialises plain text with `\n\n` between
 * paragraphs and `\n` for in-paragraph line breaks (see
 * `backend/app/services/outreach/renderer.py`). Naively splitting that
 * text on `\n` (the old `plainTextToDoc`) multiplied every blank line —
 * a single `[enter][enter]` round-tripped into THREE empty paragraphs.
 *
 * For email we seed from the rendered HTML instead, which preserves the
 * exact paragraph structure AND inline formatting (bold/italic/underline);
 * the preview-only variable/AI color spans are dropped by the parser
 * (StarterKit has no color mark), leaving clean final copy. For channels
 * with no HTML (LinkedIn / task notes) we reconstruct paragraphs from the
 * `\n\n` / `\n` convention so spacing matches 1:1.
 */

import { generateJSON } from "@tiptap/core";
import StarterKit from "@tiptap/starter-kit";

/** Minimal ProseMirror doc shape (kept local to avoid importing the
 * heavy TipTap editor module just for a type). Assignable to the
 * `StepEditor` `value` prop (`Record<string, unknown>`). */
export type ProseMirrorDoc = { type: "doc"; content: unknown[] };

// StarterKit v3 already bundles Underline; adding it again registers it twice.
const PARSE_EXTENSIONS = [
  StarterKit.configure({ codeBlock: false, heading: false }),
];

const EMPTY_DOC: ProseMirrorDoc = {
  type: "doc",
  content: [{ type: "paragraph" }],
};

/** Parse rendered email HTML into a ProseMirror doc (exact structure). */
export function finalHtmlToDoc(
  html: string | null | undefined,
): ProseMirrorDoc {
  if (!html || !html.trim()) return EMPTY_DOC;
  if (typeof window === "undefined") return finalTextToDoc(stripTags(html));
  try {
    return generateJSON(liftBlockBreaks(html), PARSE_EXTENSIONS) as ProseMirrorDoc;
  } catch {
    return finalTextToDoc(stripTags(html));
  }
}

/**
 * The renderer emits a bare ``<br>`` for a blank line between paragraphs (an
 * empty ``<p>`` collapses in email clients). ProseMirror parses a standalone,
 * block-level ``<br>`` into a *paragraph containing a hardBreak*, which the
 * editor then renders as TWO blank lines (the hardBreak + the paragraph's
 * trailing break). Convert each block-level ``<br>`` into an empty ``<p></p>``
 * first so it round-trips as exactly one blank line. In-paragraph ``<br>``
 * (Shift+Enter) is left untouched.
 */
function liftBlockBreaks(html: string): string {
  const template = document.createElement("template");
  template.innerHTML = html;
  template.content.querySelectorAll("br").forEach((br) => {
    if (br.parentNode === template.content) {
      br.replaceWith(document.createElement("p"));
    }
  });
  return template.innerHTML;
}

/**
 * Reconstruct a doc from the renderer's plain-text convention:
 * `\n\n` separates paragraphs, a single `\n` is an in-paragraph line
 * break. This keeps blank-line spacing identical to what will send.
 */
export function finalTextToDoc(
  text: string | null | undefined,
): ProseMirrorDoc {
  if (!text || text.length === 0) return EMPTY_DOC;
  const paragraphs = text.split("\n\n");
  const content = paragraphs.map((para) => {
    if (para.length === 0) return { type: "paragraph" as const };
    const lines = para.split("\n");
    const inline: Array<Record<string, unknown>> = [];
    lines.forEach((line, idx) => {
      if (idx > 0) inline.push({ type: "hardBreak" });
      if (line.length > 0) inline.push({ type: "text", text: line });
    });
    return inline.length > 0
      ? { type: "paragraph" as const, content: inline }
      : { type: "paragraph" as const };
  });
  return { type: "doc", content };
}

function stripTags(html: string): string {
  let out = html
    .replace(/<\/(p|div)>/gi, "\n\n")
    .replace(/<br\s*\/?>/gi, "\n");
  // Strip remaining tags repeatedly until the string stabilises, so a single
  // pass can't leave a reassembled tag (e.g. "<<p>p>"). This is the canonical
  // fix for CodeQL's incomplete-multi-character-sanitization. The result is
  // only ever seeded as plain text into a ProseMirror doc, never rendered as
  // HTML, but the fixpoint loop keeps the stripping correct regardless.
  let prev: string;
  do {
    prev = out;
    out = out.replace(/<[^>]*>/g, "");
  } while (out !== prev);
  return out.replace(/\n{3,}/g, "\n\n").trim();
}
